import React, { useEffect, useId, useMemo, useState } from "react";
import {
  CreatableSelect,
  CreatableSelectOption,
} from "@components/creatableSelect/CreatableSelect";
import {
  CreateLabResultInput,
  LabIndicatorDefinition,
  Laboratory,
  LabResult,
  UpdateLabResultInput,
} from "generated/graphql";
import { LaboratoryService } from "@modules/laboratory/laboratory.service";
import { DependencyType } from "@di/DependencyType";
import { useInjection } from "@di/DependencyContext";
import { LoadingState } from "@util/loadingstate.util";
import { parseServerError } from "@util/parseError";
import { toast } from "sonner";
import { Label } from "~/components/ui/label";
import { Input } from "~/components/ui/input";

export interface LabResultFieldProps {
  labResult?: LabResult;
  indicator?: Partial<LabIndicatorDefinition>;
  laboratories: Laboratory[];
  soilSampleId?: string;
  onCreateLaboratory: (lab: Laboratory) => void;
  onChangeUpdateInput?: (input: UpdateLabResultInput) => void;
  onChangeCreateInput?: (input: CreateLabResultInput) => void;
  disabled?: boolean;
}

export const LabResultField: React.FC<LabResultFieldProps> = (props) => {
  const { labResult, indicator } = props;
  const { onChangeCreateInput, onChangeUpdateInput } = props;
  const [loadingState, setLoadingState] = useState<LoadingState>(
    LoadingState.Idle
  );
  const [selectedLaboratory, setSelectedLaboratory] = useState<
    Laboratory | undefined
  >(labResult?.laboratory ?? undefined);
  const [value, setValue] = useState<string>(labResult?.value ?? "");
  const laboratoryService = useInjection<LaboratoryService>(
    DependencyType.LaboratoryService
  );

  useEffect(() => {
    setSelectedLaboratory(labResult?.laboratory ?? undefined);
    setValue(labResult?.value ?? "");
  }, [labResult?.id, labResult?.laboratory?.id, labResult?.value]);

  const laboratoryOptions = useMemo<CreatableSelectOption[]>(
    () =>
      props.laboratories.map((lab) => ({
        value: lab.id,
        label: lab.name,
      })),
    [props.laboratories]
  );

  const selectedOption = useMemo<CreatableSelectOption | undefined>(
    () =>
      selectedLaboratory
        ? {
            value: selectedLaboratory.id,
            label: selectedLaboratory.name,
          }
        : undefined,
    [selectedLaboratory]
  );

  const handleCreateLaboratory = async (name: string) => {
    try {
      setLoadingState(LoadingState.Loading);
      const created = await laboratoryService.create({ name });
      if (created) {
        props.onCreateLaboratory(created as Laboratory);
        setSelectedLaboratory(created as Laboratory);
        return { value: created.id, label: created.name };
      }
    } catch (e) {
      const parsed = parseServerError(e);
      toast.error(parsed.message);
      setLoadingState(LoadingState.Error);
      return undefined;
    } finally {
      setLoadingState(LoadingState.Idle);
    }
  };

  useEffect(() => {
    if (labResult) {
      const updateInput: UpdateLabResultInput = {
        id: labResult.id,
        value,
        laboratoryId: selectedLaboratory?.id ?? "",
      };
      onChangeUpdateInput?.(updateInput);
    } else if (indicator) {
      const createInput: CreateLabResultInput = {
        value,
        soilSampleId: "",
        indicatorId: indicator.id ?? "",
        laboratoryId: selectedLaboratory?.id ?? "",
      };
      onChangeCreateInput?.(createInput);
    }
  }, [
    indicator?.id,
    labResult?.id,
    selectedLaboratory?.id,
    value,
    onChangeCreateInput,
    onChangeUpdateInput,
  ]);

  const generatedId = useId();
  const inputId = useMemo(() => {
    if (labResult) {
      return `lab-result-${labResult.id}`;
    }
    if (indicator?.id) {
      return `lab-result-${indicator.id}`;
    }
    return generatedId;
  }, [generatedId, indicator?.id, labResult?.id]);

  const unit = labResult?.indicator?.unit ?? indicator?.unit ?? "";

  return (
    <div className="flex w-full flex-col gap-3 rounded-lg border p-4">
      <Label
        htmlFor={inputId}
        className="text-sm font-semibold text-muted-foreground"
      >
        {labResult?.indicator?.name ?? indicator?.name ?? ""}
      </Label>
      <div className="grid gap-1 md:grid-cols-2 md:items-center">
        <div className="flex items-center gap-2">
          <Input
            id={inputId}
            value={value}
            onChange={(event) => setValue(event.target.value)}
            disabled={props.disabled}
          />
          <span className="whitespace-nowrap text-sm text-muted-foreground">
            {unit}
          </span>
        </div>
        <CreatableSelect
          options={laboratoryOptions}
          onCreateOption={handleCreateLaboratory}
          onChange={(option) => {
            const nextLab = props.laboratories.find(
              (lab) => lab.id === option?.value
            );
            setSelectedLaboratory(nextLab);
          }}
          value={selectedOption}
          loading={loadingState === LoadingState.Loading || props.disabled}
          disabled={loadingState === LoadingState.Loading || props.disabled}
          placeholder="Laboratory"
        />
      </div>
    </div>
  );
};
